import { scaleLinear } from 'd3-scale';
import { extentLinear } from '@d3fc/d3fc-extent';
import { GroupedSeriesItem, GroupedSeries } from '@d3fc/d3fc-group';
import {
    seriesSvgGrouped,
    seriesSvgBar,
    AnySelection,
    seriesSvgLine,
} from '@d3fc/d3fc-series';
import { KeyedStreamItem } from 'communication/types/StreamElement';
import { convertToMilliseconds } from 'utils/components/TimeSeriesChart/timeSeriesTimeScale';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';

type Accessor<T> = (d: T) => number | undefined;

export function generateScaledLineSeries<T extends TimeSeriesChartDataItem>(
    dataSeries: T[],
    selectFn: Accessor<T>,
    yAxisDomain: [number, number],
    lineClass: string,
    additionalAccessors?: Accessor<T>[]
) {
    const accessors = additionalAccessors
        ? [selectFn, ...additionalAccessors]
        : [selectFn];
    const domain = extentLinear().accessors(accessors)(dataSeries);

    const scaleFn = scaleLinear().domain(domain).range(yAxisDomain);

    return seriesSvgLine()
        .crossValue((d: T) => d.time)
        .mainValue((d: T) => {
            const rawValue = selectFn(d);
            return rawValue !== undefined ? scaleFn(rawValue) : undefined;
        })
        .decorate((selection: AnySelection) => {
            selection.classed(lineClass, true);
        });
}

export function generateGroupedBarSeries<T>(
    seriesLength: number,
    chartWidth: number,
    barClasses: string[],
    getWidthPercentage: (d: T) => number | undefined
) {
    type BarChartItem = GroupedSeriesItem<T>;

    const normalBarWidth = chartWidth / (barClasses.length * seriesLength);

    return seriesSvgGrouped(seriesSvgBar())
        .crossValue((d: BarChartItem) => d[0])
        .mainValue((d: BarChartItem) => d[1])
        .bandwidth((d: BarChartItem) => {
            const percentage = d.data && getWidthPercentage(d.data);
            return normalBarWidth * (percentage ? percentage : 1);
        })
        .decorate(
            (
                selection: AnySelection,
                data: GroupedSeries<T>[],
                index: number
            ) => {
                const barsSelection = selection.select('path');
                barClasses.forEach((barClass, barIndex) => {
                    barsSelection.classed(barClass, () => index === barIndex);
                });
            }
        );
}

export function computeTimeSeriesBarWidthPercentage<T extends KeyedStreamItem>(
    currentIndex: number,
    sortedTimeSeries: T[],
    bucketSizeInMins: number,
    breaksInTrading: TimeSeriesDisableRange[]
): number | undefined {
    const currentTimeInMins = sortedTimeSeries[currentIndex]._i;
    const nextTimeInMins =
        currentIndex === sortedTimeSeries.length - 1
            ? currentTimeInMins + bucketSizeInMins
            : sortedTimeSeries[currentIndex + 1]._i;

    const bucketSizeInMs = convertToMilliseconds(bucketSizeInMins);

    const bucketStartTime = convertToMilliseconds(currentTimeInMins);
    const bucketEndTime = bucketStartTime + bucketSizeInMs;

    const tradingBreakResult = getBarWidthPercentageConsideringTradingBreaks(
        bucketStartTime,
        bucketEndTime,
        bucketSizeInMs,
        breaksInTrading
    );

    return tradingBreakResult
        ? tradingBreakResult
        : computeBucketBarWidthPercentage(
              currentTimeInMins,
              nextTimeInMins,
              bucketSizeInMins
          );
}

export function computeBucketBarWidthPercentage(
    startTime: number,
    endTime: number,
    bucketSize: number
): number | undefined {
    const currentBucketWidth = endTime - startTime;

    return currentBucketWidth <= bucketSize
        ? currentBucketWidth / bucketSize
        : undefined;
}

function getBarWidthPercentageConsideringTradingBreaks(
    bucketStartTime: number,
    bucketEndTime: number,
    bucketSizeInMs: number,
    breaksInTrading: TimeSeriesDisableRange[]
): number | undefined {
    for (let x = 0; x < breaksInTrading.length; ++x) {
        const tradingBreak = breaksInTrading[x];

        if (
            bucketStartTime < tradingBreak.from &&
            tradingBreak.from < bucketEndTime
        ) {
            return (tradingBreak.from - bucketStartTime) / bucketSizeInMs;
        }
    }

    return undefined;
}
